import type {ElementData} from '@Libraries/add-media-library';
import type {BasePanel} from '@Components/panel/panel.types';
import type {AiMenuOpenSource} from '@Libraries/ga-events';
import type {AttachmentDropdownConfig} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentDivider, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';

export interface AiImagePanelProps extends BasePanel {
  className?: string;
  gridID?: string;
  selectionID?: string;
  populateRecentlyAddedOnItemAdd?: boolean;
  shouldLoadDataUrlImage?: boolean;
  mediaType?: MediaTypePrompt;
  showBackButton?: boolean;
  showInPanelUpsell?: boolean;
  enableDragToCanvas?: boolean;
  showEditWithAiOnGeneratedImages?: boolean;
  /** Caps the mobile multi-selection; omit for unlimited. Mirrors the AI Videos panel's host-driven cap (replace flows pass 1). Desktop always adds a clicked image directly. */
  maxSelectionNumber?: number;
  /** Deliver the whole selection to onAddSelected in one call (e.g. N picks become one slideshow). The default streams each item as soon as its upload settles. */
  addInBulk?: boolean;
  /** Clicks accumulate a multi-selection that the footer's Add Selected delivers at once (e.g. the desktop slideshow picker). Off by default: a clicked image is added to the design immediately. Mobile hosts multi-select regardless. */
  enableMultiSelection?: boolean;
  /** The editor surface hosting this panel, reported as the `source` of its generate and add GA4 events. */
  ga4Source: AiMenuOpenSource;
  onAddSelected(items: ElementData[]): void | Promise<void>;
  onClose?(): void;
}

export const AI_IMAGE_UPSELL_PANEL_HEIGHT_PX = 616;

export const AI_IMAGE_FOOTER_OPTIONS_PANEL_ID = 'ai-image-footer-options-panel';
export const AI_IMAGE_MAX_REFERENCE_IMAGES = 4;
export const CURRENT_PAGE_SNAPSHOT_MAX_DIMENSION = 1024;
export const CURRENT_PAGE_SNAPSHOT_REFERENCE_SLOTS = 1;
export const CURRENT_PAGE_SNAPSHOT_FILENAME = 'current-design-page.png';
export const SEEDREAM_4_5_MAX_REFERENCE_IMAGES = 1;
export const AI_IMAGE_MAX_PROMPT_LENGTH = 1000;
export const AI_IMAGE_ATTACHMENT_DROPDOWN_OPTIONS: AttachmentDropdownConfig = [
  PromptAttachmentType.UPLOAD_FROM_DEVICE,
  PromptAttachmentType.CAPTURE_PHOTO,
  PromptAttachmentDivider.HORIZONTAL_LINE,
  PromptAttachmentType.IMAGE,
  PromptAttachmentType.DESIGN,
];

export enum AiImageModelOption {
  StabilityAICore = 1,
  StabilityAISDXL = 2,
  DeepInfraFluxSchnell = 3,
  StabilityAIUltra = 4,
  Auto = 5,
  Gemini2_5 = 6,
  DeepInfraFlux2Pro = 7,
  ModelArkSeedream5_0Lite = 8,
  OpenAIGptImage2 = 9,
  GeminiNanoBanana2 = 10,
  DeepInfraSeedream4_5 = 11,
  GeminiNanoBanana2Lite = 12,
  OpenAIGptImage2_5 = 13,
}

export enum AiImageFilterOptions {
  Styles = 'styles',
  AspectRatio = 'aspect-ratio',
  AiModel = 'ai-model',
}

export enum AiImageStylesOptions {
  Photographic = 'photographic',
  DigitalArt = 'digital-art',
  Model3d = '3d-model',
  Cinematic = 'cinematic',
  ComicBook = 'comic-book',
  NeonPunk = 'neon-punk',
  LineArt = 'line-art',
  FantasyArt = 'fantasy-art',
  Anime = 'anime',
  PixelArt = 'pixel-art',
  AnalogFilm = 'analog-film',
}

export enum AiImageAspectRatioOptions {
  Square = 'SQUARE',
  LandScape = 'LANDSCAPE',
  Portrait = 'PORTRAIT',
}

export const enum MediaTypePrompt {
  PHOTO = 'photo',
  LOGO = 'logo',
  TEXT_ART = 'text-art',
  STICKER = 'sticker',
}

export const DEFAULT_SELECTED_AI_IMAGE_MODEL = AiImageModelOption.Auto;

export interface AiImageModelCardConfig {
  model: AiImageModelOption;
  labelKey: string;
  descriptionKey: string;
}

export const AI_IMAGE_MODEL_CARDS: readonly AiImageModelCardConfig[] = [
  {model: AiImageModelOption.Auto, labelKey: 'pmwjs_auto', descriptionKey: 'pmwjs_ai_model_auto_desc'},
  {model: AiImageModelOption.GeminiNanoBanana2Lite, labelKey: 'pmwjs_nano_banana_2_lite', descriptionKey: 'pmwjs_ai_model_nano_banana_2_lite_desc'},
  {model: AiImageModelOption.DeepInfraFlux2Pro, labelKey: 'pmwjs_flux_2_pro', descriptionKey: 'pmwjs_ai_model_flux_2_pro_desc'},
  {model: AiImageModelOption.OpenAIGptImage2_5, labelKey: 'pmwjs_gpt_image_2_5', descriptionKey: 'pmwjs_ai_model_gpt_image_2_5_desc'},
  {model: AiImageModelOption.GeminiNanoBanana2, labelKey: 'pmwjs_nano_banana_2', descriptionKey: 'pmwjs_ai_model_nano_banana_2_desc'},
  {model: AiImageModelOption.DeepInfraSeedream4_5, labelKey: 'pmwjs_seedream_4_5', descriptionKey: 'pmwjs_ai_model_seedream_4_5_desc'},
];
